import React, { useMemo } from 'react';
import { Box, Grid, Typography, Chip } from '@mui/material';
import {
  Favorite as FavoriteIcon,
  Event as SessionIcon,
  Store as ExhibitorIcon,
  Person as SpeakerIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { FavoritesMetrics, CHART_COLORS } from '../../types/dashboard.types';
import ChartCard from './ChartCard';
import SectionHeader from './SectionHeader';
import EmptyState from './EmptyState';

export interface FavoritesMetricsSectionProps {
  data: FavoritesMetrics;
  loading?: boolean;
}

const FavoritesMetricsSection: React.FC<FavoritesMetricsSectionProps> = ({
  data,
  loading = false,
}) => {
  const favTypeData = useMemo(
    () =>
      data.favoritesByType.map((item, index) => ({
        name: item.type,
        count: item.count,
        fill: CHART_COLORS.palette[index % CHART_COLORS.palette.length],
      })),
    [data.favoritesByType]
  );

  const hasFavTypeData = favTypeData.length > 0;
  const hasSessions = data.topFavoritedSessions.length > 0;
  const hasExhibitors = data.topFavoritedExhibitors.length > 0;
  const hasSpeakers = data.topFavoritedSpeakers.length > 0;

  const renderTopList = (
    items: Array<{ id: number; name: string; favoriteCount: number }>,
    icon: React.ReactNode,
    emptyMessage: string
  ) => {
    if (items.length === 0) {
      return <EmptyState message={emptyMessage} height={180} />;
    }

    return (
      <Box sx={{ p: 1 }}>
        {items.slice(0, 5).map((item, index) => (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              mb: 0.5,
              bgcolor: '#f9fafb',
              borderRadius: 1,
              '&:hover': { bgcolor: '#f3f4f6' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor:
                    index === 0
                      ? '#fef3c7'
                      : index === 1
                        ? '#e5e7eb'
                        : index === 2
                          ? '#fed7aa'
                          : '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color:
                    index === 0
                      ? '#d97706'
                      : index === 1
                        ? '#6b7280'
                        : index === 2
                          ? '#c2410c'
                          : '#9ca3af',
                }}
              >
                {index + 1}
              </Box>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: '#111827',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 150,
                }}
              >
                {item.name}
              </Typography>
            </Box>
            <Chip
              icon={<FavoriteIcon sx={{ fontSize: 14 }} />}
              label={item.favoriteCount}
              size="small"
              sx={{
                bgcolor: '#fce7f3',
                color: '#db2777',
                fontSize: '0.75rem',
                height: 24,
                '& .MuiChip-icon': { color: '#db2777' },
              }}
            />
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <SectionHeader title="Favorites" icon={<FavoriteIcon />} />

      <Grid container spacing={3}>
        {/* Favorites by Type Chart */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Favorites by Type"
            subtitle="Distribution of favorited items"
            height={280}
            loading={loading}
          >
            {!hasFavTypeData ? (
              <EmptyState
                message="No favorites data available"
                icon={<FavoriteIcon sx={{ fontSize: 32, color: '#9ca3af' }} />}
                height={250}
              />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={favTypeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#6b7280" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>

        {/* Top Favorited Sessions */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Top Favorited Sessions"
            subtitle="Most popular sessions"
            height={280}
            loading={loading}
          >
            {renderTopList(
              data.topFavoritedSessions,
              <SessionIcon />,
              'No session favorites'
            )}
          </ChartCard>
        </Grid>

        {/* Top Favorited Exhibitors */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Top Favorited Exhibitors"
            subtitle="Most popular exhibitors"
            height={280}
            loading={loading}
          >
            {renderTopList(
              data.topFavoritedExhibitors,
              <ExhibitorIcon />,
              'No exhibitor favorites'
            )}
          </ChartCard>
        </Grid>

        {/* Top Favorited Speakers */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Top Favorited Speakers"
            subtitle="Most popular speakers"
            height={280}
            loading={loading}
          >
            {renderTopList(
              data.topFavoritedSpeakers,
              <SpeakerIcon />,
              'No speaker favorites'
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FavoritesMetricsSection;
